/**
 * recentlyAccessed — per-device "last opened" stamps for sidebar recency.
 *
 * The server timestamps (`updatedAt` on networks, `lastActivityAt` on rooms)
 * track MODIFICATION / RUN, not "I just opened this." So an old network you
 * open from search wouldn't float up. This records a local, per-device
 * "last accessed" stamp on open; the sidebar blends it as
 * ``max(lastAccessed, activityTs)`` ("access wins") so a searched-and-opened
 * item floats to the top and naturally drifts down as OTHER items get opened.
 *
 * Deliberately localStorage (per-device, private): opening something is a
 * navigation, not a mutation — it must NOT look like an edit to other users or
 * bump a shared server field. Best-effort: any storage error is swallowed
 * (private-mode / quota) and the feature degrades to server timestamps only.
 */

const KEY = 'sf.recentlyAccessed.v1';
const MAX_ENTRIES = 200; // cap so the map can't grow unbounded

function _read() {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function _write(map) {
  try {
    if (typeof localStorage === 'undefined') return;
    // Trim to the most-recent MAX_ENTRIES so the blob stays small.
    const entries = Object.entries(map);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1] - a[1]);
      map = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
    }
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* ignore — degrade to server timestamps */
  }
}

/** Stamp ``id`` as accessed now. ``nowMs`` overridable for tests. */
export function markAccessed(id, nowMs) {
  const key = String(id || '').trim();
  if (!key) return;
  const map = _read();
  map[key] = typeof nowMs === 'number' ? nowMs : Date.now();
  _write(map);
}

/** The whole {id: epochMs} map (one read; pass to the sort so it's stable
 *  within a render). {} when unavailable. */
export function getAccessMap() {
  return _read();
}

/** Access stamp for one id as an ISO string (so it composes with the ISO
 *  ``getActivity`` used by computeRecentSearchList), or '' if never accessed. */
export function accessedIso(map, id) {
  const t = map && map[String(id || '')];
  return typeof t === 'number' && Number.isFinite(t) ? new Date(t).toISOString() : '';
}
